import { useEffect, useRef, useState } from "react";
import { IoMicOutline, IoStopCircle, IoTrashOutline } from "react-icons/io5";
import { toast } from "sonner";
import { RED } from "@/components/DojoShell";

/**
 * Enregistreur de mémo vocal réutilisable.
 * onRecorded(file) reçoit un File audio (webm) prêt à être uploadé.
 */
export default function VoiceRecorder({ onRecorded, testid = "voice-recorder", compact = false }) {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => () => { try { recRef.current?.stream?.getTracks().forEach((t) => t.stop()); } catch {} clearInterval(timerRef.current); }, []);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      recRef.current = rec; rec.stream = stream; chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        const file = new File([blob], `memo-vocal-${Date.now()}.webm`, { type: "audio/webm" });
        setPreviewUrl(URL.createObjectURL(blob));
        onRecorded?.(file);
      };
      rec.start();
      setRecording(true); setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    } catch { toast.error("Micro inaccessible — autorise le micro dans ton navigateur."); }
  };
  const stop = () => { try { recRef.current?.stop(); } catch {} clearInterval(timerRef.current); setRecording(false); };
  const reset = () => { setPreviewUrl(null); setElapsed(0); onRecorded?.(null); };

  const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  if (previewUrl) {
    return (
      <div style={s.wrap} data-testid={`${testid}-preview`}>
        <audio controls src={previewUrl} style={{ flex: 1, height: 38 }} />
        <button style={s.del} onClick={reset} data-testid={`${testid}-reset`} title="Supprimer"><IoTrashOutline size={17} /></button>
      </div>
    );
  }
  return (
    <div style={s.wrap}>
      {recording ? (
        <>
          <span style={s.pulse} />
          <span style={s.timer}>{mmss(elapsed)}</span>
          <button style={s.stop} onClick={stop} data-testid={`${testid}-stop`}><IoStopCircle size={18} /> Arrêter</button>
        </>
      ) : (
        <button style={compact ? s.micCompact : s.mic} onClick={start} data-testid={`${testid}-start`}>
          <IoMicOutline size={18} /> {compact ? "Vocal" : "Enregistrer un vocal"}
        </button>
      )}
    </div>
  );
}

const s = {
  wrap: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  mic: { display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 16px", borderRadius: 999, border: `1.5px solid ${RED}`, background: "transparent", color: RED, fontWeight: 800, fontSize: 13.5, cursor: "pointer" },
  micCompact: { display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 13px", borderRadius: 10, border: `1.5px solid ${RED}`, background: "transparent", color: RED, fontWeight: 800, fontSize: 13, cursor: "pointer" },
  stop: { display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 16px", borderRadius: 999, border: "none", background: RED, color: "#fff", fontWeight: 800, fontSize: 13.5, cursor: "pointer" },
  timer: { fontWeight: 900, fontSize: 15, color: "var(--text-primary)", fontVariantNumeric: "tabular-nums" },
  pulse: { width: 12, height: 12, borderRadius: "50%", background: RED, animation: "vrpulse 1s ease-in-out infinite" },
  del: { width: 38, height: 38, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 10, border: "1px solid var(--border-c)", background: "var(--bg-tertiary)", color: RED, cursor: "pointer" },
};

if (typeof document !== "undefined" && !document.getElementById("vr-kf")) {
  const el = document.createElement("style"); el.id = "vr-kf";
  el.textContent = "@keyframes vrpulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.4;transform:scale(0.8)}}";
  document.head.appendChild(el);
}
